import React, { useEffect, useMemo, useState } from "react";
import "../inward-styles.css";
import { InwardAttachmentUpload } from "./InwardAttachmentUpload";
import Icon from "@mdi/react";
import {
  mdiAlert,
  mdiAlertCircle,
  mdiAlertOutline,
  mdiCalendarBlankOutline,
  mdiCheckCircleOutline,
  mdiChevronLeft,
  mdiHistory,
  mdiPackageVariantClosed,
  mdiWarehouse,
} from "@mdi/js";
import {
  formatAmountWithCommas,
  formatDateYYYYMMDD,
} from "../../content_panel/utils/helpers";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";
import RequiredMark from "../../../new_components/common/required_mark";
import LinkedInwardsTable from "./LinkedInwards";
import CurrencyBadge from "./CurrencyBadge";
import {
  DefaultWarehouseInward,
  ItemWarehouseModel,
  WarehouseInwardModel,
  LinkedInwardModel,
} from "../model/warehouse_inward_models";
import { useWarehouseInward } from "../contexts/InwardContext";
import {
  CreatePurchaseOrderWarehouseInwardRepo,
  FetchPOInwardOrderDetailsByIdRepo,
  GetLinkedInwardForPOAPI,
  GetPOInwardedDevicesAPI,
} from "../warehouse_module_repo";
import {
  calculateIsBlockedByDispute,
  getInwardStatusClass,
  computeInwardTaxTotals,
} from "../warehouse_helper";
import { useHistory, useParams } from "react-router-dom";
import { urls } from "../../../../utils/urls";
import PastDisputes from "./PastDisputes";
import ConfirmationModal from "../common-components/ConfirmationModal";
import RaiseDisputeModal, { DisputeData } from "./RaiseDisputeModal";
import { raiseInwardDisputeApi } from "../warehouse_module_repo";
import { DisputedItemModel } from "../model/quarantine_model";
import { StockTransferSubtype } from "../model/warehouse_outward_model";
import InfraInwardReceiveItemCard from "./InfraInwardItemCardGRN";

interface DeviceSelection {
  matched: string[];
  unmatched: string[];
  quantity: number;
}

type MainTabType = "items" | "grn-history";

type RouteParams = {
  type: string;
  inward_order_id: string;
};

const getInwardItemRowKey = (
  item: ItemWarehouseModel,
  index: number,
): string => {
  return `${item.inventoryItemCode}__${item.itemPrice}__${index}`;
};

const getLinkedInwardItemMatchKey = (itemCode: string, itemPrice?: number) => {
  return `${itemCode}__${itemPrice ?? 0}`;
};

const InfraPOInwardReceivePage = () => {
  // Form state - MANAGED LOCALLY
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [deviceSelections, setDeviceSelections] = useState<
    Record<string, DeviceSelection>
  >({});
  const [qtyErrors, setQtyErrors] = useState<Record<string, string>>({});
  const [invoiceQtys, setInvoiceQtys] = useState<Record<string, number>>({});
  const [invoiceRates, setInvoiceRates] = useState<Record<string, number>>({});
  const [physicalQtys, setPhysicalQtys] = useState<Record<string, number>>({});
  const [invoiceTotalAmounts, setInvoiceTotalAmounts] = useState<
    Record<string, number>
  >({});
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Dispute UI state
  const [isDisputeOpen, setIsDisputeOpen] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkedPoInwards, setLinkedPoInwards] = useState<LinkedInwardModel[]>(
    [],
  );
  const [inwardedDevicesByItem, setInwardedDevicesByItem] = useState<
    Record<string, string[]>
  >({});
  const [inwardedDevicesByItemAndPrice, setInwardedDevicesByItemAndPrice] =
    useState<Record<string, string[]>>({});
  const [activeTab, setActiveTab] = useState<MainTabType>("items");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDisputeSubmitting, setIsDisputeSubmitting] = useState(false);

  const { showToast } = useToast();
  const { setCurrentInwardOrder } = useWarehouseInward();
  const [inwardOrder, setInwardOrder] = useState<WarehouseInwardModel>(
    DefaultWarehouseInward,
  );

  const isBlockedByDispute = useMemo(
    () =>
      calculateIsBlockedByDispute(
        inwardOrder.past_disputes,
        inwardOrder.is_disputed,
      ),
    [inwardOrder.past_disputes, inwardOrder.is_disputed],
  );

  const { type, inward_order_id } = useParams<RouteParams>();

  const isRevised =
    inward_order_id?.toLowerCase()?.startsWith("po-") &&
    inward_order_id?.split("-")?.length > 2;

  const inwardItems: ItemWarehouseModel[] = React.useMemo(() => {
    return inwardOrder.items || [];
  }, [type, inwardOrder]);

  const itemCodeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    inwardItems?.forEach((item) => {
      counts[item.inventoryItemCode] =
        (counts[item.inventoryItemCode] || 0) + 1;
    });
    return counts;
  }, [inwardItems]);

  const getInwardedDevicesForItem = (item: ItemWarehouseModel): string[] => {
    const hasDuplicateCode = (itemCodeCounts[item.inventoryItemCode] || 0) > 1;
    if (
      hasDuplicateCode &&
      item.itemPrice !== undefined &&
      item.itemPrice !== null
    ) {
      const priceKey = `${item.inventoryItemCode}::${item.itemPrice.toFixed(2)}`;
      return inwardedDevicesByItemAndPrice[priceKey] ?? [];
    }
    return inwardedDevicesByItem[item.inventoryItemCode] ?? [];
  };

  const orderTotals = useMemo(() => {
    let subtotal = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    inwardItems?.forEach((item, index) => {
      const rowKey = getInwardItemRowKey(item, index);
      const qty = invoiceQtys[rowKey] || 0;
      const rate = invoiceRates[rowKey] || 0;

      // Parse unit divisor from itemUnit (e.g., "100 Number" -> 100) or default to 1
      const unitDivisor = parseInt(item.itemUnit?.replace(/\D/g, "")) || 1;

      const itemSubtotal = (qty * rate) / unitDivisor;
      subtotal += itemSubtotal;

      if (item.gst_applicable && item.tax_detail) {
        // Calculate GST as percentage of item subtotal
        cgst += (itemSubtotal * (item.tax_detail.cgst || 0)) / 100;
        sgst += (itemSubtotal * (item.tax_detail.sgst || 0)) / 100;
        igst += (itemSubtotal * (item.tax_detail.igst || 0)) / 100;
      }
    });

    return {
      subtotal,
      cgst,
      sgst,
      igst,
      total: subtotal + cgst + sgst + igst,
    };
  }, [inwardItems, invoiceQtys, invoiceRates, type, inwardOrder]);

  const receivedQtyByRowKey = useMemo(() => {
    const receivedByMatchKey = new Map<string, number>();

    linkedPoInwards.forEach((linkedInward) => {
      linkedInward.items?.forEach((linkedItem) => {
        const matchKey = getLinkedInwardItemMatchKey(
          linkedItem.item_code,
          linkedItem.itemPrice,
        );
        const current = receivedByMatchKey.get(matchKey) || 0;
        receivedByMatchKey.set(
          matchKey,
          current + (linkedItem.accepted_qty || 0),
        );
      });
    });

    return inwardItems.reduce<Record<string, number>>((acc, item, index) => {
      const rowKey = getInwardItemRowKey(item, index);
      const matchKey = getLinkedInwardItemMatchKey(
        item.inventoryItemCode,
        item.itemPrice,
      );

      // item.receivedQty is the authoritative, persisted ledger value —
      // prefer it. Only fall back to summing GRN History (matched by
      // item_code+price) when it's missing, e.g. for older inward docs that
      // predate that field being populated.
      acc[rowKey] = item.receivedQty ?? receivedByMatchKey.get(matchKey) ?? 0;

      return acc;
    }, {});
  }, [inwardItems, linkedPoInwards]);

  const getPendingQty = (item: ItemWarehouseModel, rowKey: string) => {
    const receivedQty = receivedQtyByRowKey[rowKey] ?? 0;
    return Math.max((item.requestedQty ?? 0) - receivedQty, 0);
  };

  const history = useHistory();

  // Fetch previously inwarded devices
  const fetchInwardedDevices = async () => {
    setLoading(true);
    try {
      if (type === "PO") {
        const res = await GetPOInwardedDevicesAPI(inward_order_id);
        if (res.success && res.data?.devicesByItem) {
          setInwardedDevicesByItem(res.data.devicesByItem);
        }
        if (res.success && res.data?.devicesByItemAndPrice) {
          setInwardedDevicesByItemAndPrice(res.data.devicesByItemAndPrice);
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch inwarded devices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle device selection from child component
  const handleDeviceSelection = (
    rowKey: string,
    item: ItemWarehouseModel,
    selection: DeviceSelection,
  ) => {
    const pending = getPendingQty(item, rowKey);

    const safeSelection = {
      ...selection,
      quantity: Math.min(selection.quantity, pending),
      matched: selection.matched.slice(0, pending),
    };

    setDeviceSelections((prev) => ({
      ...prev,
      [rowKey]: safeSelection,
    }));

    setPhysicalQtys((prev) => ({
      ...prev,
      [rowKey]: safeSelection.quantity,
    }));

    setQtyErrors((prev) => {
      const updated = { ...prev };
      delete updated[rowKey];
      return updated;
    });
  };

  const handleRateChange = (rowKey: string, value: number) => {
    setInvoiceRates((prev) => ({ ...prev, [rowKey]: value }));
  };

  const handleInvoiceQtyChange = (rowKey: string, value: number) => {
    setInvoiceQtys((prev) => ({ ...prev, [rowKey]: value }));
  };

  const handleTotalAmountChange = (rowKey: string, value: number) => {
    setInvoiceTotalAmounts((prev) => ({ ...prev, [rowKey]: value }));
  };

  const handlePhysicalQtyChange = (
    rowKey: string,
    item: ItemWarehouseModel,
    value: number,
  ) => {
    const pending = getPendingQty(item, rowKey);

    if (value > pending) {
      showToast({
        type: ToastType.warning,
        text: `Physical quantity cannot exceed pending quantity of ${pending}`,
      });
      return;
    }

    if (value < 0) {
      setQtyErrors((prev) => ({
        ...prev,
        [rowKey]: `Qty must be at least 0`,
      }));
    } else {
      setQtyErrors((prev) => {
        const updated = { ...prev };
        delete updated[rowKey];
        return updated;
      });
    }

    setPhysicalQtys((prev) => ({
      ...prev,
      [rowKey]: value,
    }));

    setDeviceSelections((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || { matched: [], unmatched: [] }),
        quantity: value,
      },
    }));
  };

  // Build payload from selections
  const buildPayload = () => {
    const items = inwardItems
      .map((item, index) => {
        const rowKey = getInwardItemRowKey(item, index);
        const selection = deviceSelections[rowKey];
        if (!selection || selection.quantity === 0) return null;

        const pending = getPendingQty(item, rowKey);
        const finalQty = Math.min(selection.quantity, pending);

        return {
          inventoryItemCode: item.inventoryItemCode,
          requestedQty: item.requestedQty,
          total_received_qty: finalQty,
          deviceIds: selection.matched.slice(0, finalQty),
          itemPrice: item.itemPrice,
          itemSubCategory: item.subCategory,
          itemName: item.itemName,
        };
      })
      .filter(Boolean);

    return [
      {
        id: inwardOrder.id,
        po_no: inwardOrder.inward_order_id,
        items,
        toWarehouse: inwardOrder.toWarehouse,
        invoice_number: invoiceNumber || "",
      },
    ];
  };

  // Validate form
  const validateForm = (): boolean => {
    if (type === "PO" && !invoiceNumber.trim()) {
      showToast({ type: ToastType.error, text: "Invoice number is required" });
      return false;
    }

    if (!attachments.length) {
      showToast({
        type: ToastType.error,
        text: "At least one attachment is required",
      });
      return false;
    }

    const hasItems = Object.values(deviceSelections).some(
      (s) => s.quantity > 0,
    );
    if (!hasItems) {
      showToast({
        type: ToastType.error,
        text: "At least one item must be received",
      });
      return false;
    }

    // Validate each item
    for (const [rowKey, selection] of Object.entries(deviceSelections)) {
      if (selection.quantity <= 0) continue;

      const itemIndex = inwardItems.findIndex(
        (i, index) => getInwardItemRowKey(i, index) === rowKey,
      );
      const item = itemIndex >= 0 ? inwardItems[itemIndex] : null;
      const pending = item ? getPendingQty(item, rowKey) : 0;

      if (selection.quantity > pending) {
        showToast({
          type: ToastType.error,
          text: `Quantity for ${item?.itemName} exceeds pending limit (${pending})`,
        });
        return false;
      }

      if (
        selection.matched.length > 0 &&
        selection.matched.length !== selection.quantity
      ) {
        showToast({
          type: ToastType.error,
          text: `Device count mismatch for ${item?.inventoryItemCode}`,
        });
        return false;
      }

      // Validate PO Rate vs Invoice Rate
      if (type === "PO") {
        const poRate = item?.itemPrice || 0;
        const invoiceRate = invoiceRates[rowKey] || 0;

        if (invoiceRate > 0 && poRate !== invoiceRate) {
          showToast({
            type: ToastType.error,
            text: `Rate mismatch for ${item?.itemName}: PO Rate (₹${poRate}) does not match Invoice Rate (₹${invoiceRate}). Please correct the invoice rate.`,
          });
          return false;
        }
      }

      // Finished Goods: invoice qty must equal PO qty, and scanned devices must match invoice qty
      if (item?.subCategory === "Finished Goods") {
        const invoiceQty = invoiceQtys[rowKey] || 0;
        const poQty = item?.requestedQty ?? 0;
        const scanned = selection.matched.length;

        if (invoiceQty !== poQty) {
          showToast({
            type: ToastType.error,
            text: `Invoice qty for ${item?.itemName} must match PO qty (${poQty})`,
          });
          return false;
        }

        if (scanned !== invoiceQty) {
          showToast({
            type: ToastType.error,
            text: `Scanned devices (${scanned}) must match invoice qty (${invoiceQty}) for ${item?.itemName}`,
          });
          return false;
        }
      }
    }

    return true;
  };

  const handleOpenConfirmation = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  // Submit inward
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!inwardOrder) {
      showToast({ type: ToastType.error, text: "No inward order selected" });
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      const payload = buildPayload();
      const formData = new FormData();

      formData.append("payload", JSON.stringify(payload));
      if (attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      const res = await CreatePurchaseOrderWarehouseInwardRepo(formData);

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        setShowConfirmModal(false);
        refreshInwardState();
        return;
      }

      showToast({
        type: ToastType.success,
        text: `${inwardOrder.type} inward recorded successfully`,
      });

      setShowConfirmModal(false);
      history.push(`${urls.warehouse_inward}`);
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to record inward",
      });
      refreshInwardState();
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // A failed or timed-out submit may still have been inwarded server-side (or
  // another user may have inwarded meanwhile) — reload so pending qty and the
  // already-inwarded device list are current before the user retries.
  const refreshInwardState = () => {
    if (type === "PO") fetchPOInwardDetails(inward_order_id);
    fetchLinkedInwardData();
    fetchInwardedDevices();
  };

  const handleDisputeSubmit = async (data: DisputeData) => {
    if (!inwardOrder || !data.selectedItems.length) return;

    setIsDisputeSubmitting(true);
    try {
      const results = await Promise.all(
        data.selectedItems.map(async (rowKey) => {
          const itemIndex = inwardItems.findIndex(
            (i, index) => getInwardItemRowKey(i, index) === rowKey,
          );
          const item = itemIndex >= 0 ? inwardItems[itemIndex] : null;
          if (!item) return null;

          const invoiceRate = invoiceRates[rowKey] || 0;
          const physicalQty =
            item.subCategory === "Finished Goods"
              ? deviceSelections[rowKey]?.matched.length || 0
              : physicalQtys[rowKey] || 0;

          const payload: DisputedItemModel = {
            inward_order_id: inwardOrder.inward_order_id,
            inventory_item_code: item.inventoryItemCode,
            item_name: item.itemName,
            item_type: item.subCategory,
            dispute_types: data.disputeTypes.filter((dt) => dt !== ""),
            disputed_physical_quantity: physicalQty,
            disputed_invoice_rate: invoiceRate,
            disputed_total_value: (invoiceRate || 0) * physicalQty,
            invoice_quantity: invoiceQtys[rowKey] || 0,
            po_quantity: item.requestedQty,
            po_rate: item.itemPrice || 0,
            disputed_device_ids: deviceSelections[rowKey]?.matched || [],
            warehouse_name: inwardOrder.toWarehouse.name,
            warehouse_id: inwardOrder.toWarehouse.id,
            remark: data.description,
            status: "open",
            is_disputed: true,
          };

          return raiseInwardDisputeApi(payload);
        }),
      );

      const failed = results.filter((r) => r && !r.success);
      if (failed.length > 0) {
        showToast({
          type: ToastType.error,
          text: `Failed to submit disputes: ${failed.map((f: any) => f.error).join(", ")}`,
        });
      } else {
        showToast({
          type: ToastType.success,
          text: "Dispute submitted successfully",
        });
        setIsDisputeOpen(false);
        // Refresh inward order to show "Disputed" status
        if (type === "PO") fetchPOInwardDetails(inward_order_id);

        history.push(`${urls.warehouse_inward}`);
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e.message || "Failed to submit dispute",
      });
    } finally {
      setIsDisputeSubmitting(false);
    }
  };

  const fetchPOInwardDetails = async (inward_order_id: string) => {
    setLoading(true);

    try {
      const result = await FetchPOInwardOrderDetailsByIdRepo(inward_order_id);
      if (!result.success) {
        console.log("error");
      }
      if (result.success) {
        setCurrentInwardOrder(result.data);
        setInwardOrder(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching PO inward details", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch linked PO inwards
  const fetchLinkedInwardData = async () => {
    setLoading(true);
    try {
      const ref_type = type === "PO" ? "GRN" : "Stock transfer";

      const res = await GetLinkedInwardForPOAPI(inward_order_id, ref_type);

      if (res.success) {
        setLinkedPoInwards(res.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInvoiceNumber("");
    setAttachments([]);
    setDeviceSelections({});
    setQtyErrors({});
    setInvoiceQtys({});
    setInvoiceRates({});
    setPhysicalQtys({});
    setInvoiceTotalAmounts({});
    setLinkedPoInwards([]);
    setInwardedDevicesByItem({});
    setActiveTab("items");
    setShowConfirmModal(false);
    setIsDisputeOpen(false);
    setInwardOrder(DefaultWarehouseInward);

    if (type && inward_order_id) {
      if (type === "PO") {
        fetchPOInwardDetails(inward_order_id);
      }
      fetchLinkedInwardData();
      fetchInwardedDevices();
    }
  }, [type, inward_order_id]);

  return (
    <div className="wh-mobile-shrink bg-gray-50 flex flex-col min-h-screen">
      {/* Page container */}
      <div className="px-2 py-2 flex-1">
        {/* Header */}
        <div className="flex flex-row gap-2 items-center justify-start pl-2">
          <button
            id="warehouse-inward-infra-receive-back-btn"
            onClick={() => history.push(`${urls.warehouse_inward}`)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 hover:underline"
          >
            <Icon path={mdiChevronLeft} size={0.9} />
            Back
          </button>

          <h1 className="text-3xl font-bold pl-2  border-l-4 border-myPrimaryColor ">
            Record Inward
          </h1>
        </div>

        {isBlockedByDispute && (
          <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-100 p-4 mt-4">
            <Icon
              path={mdiAlert}
              size={1}
              className="text-color-destructive shrink-0 mt-0.5"
            />

            <div className="text-sm flex-1">
              <p className="font-medium text-color-destructive">
                GRN Creation Blocked
              </p>
              {inwardOrder.past_disputes &&
              inwardOrder.past_disputes.filter((d) => d.status === "open")
                .length > 0 ? (
                <p className="text-muted-foreground mt-1">
                  This PO has unresolved disputes. No further GRNs can be
                  created until a Revised PO is issued.
                </p>
              ) : (
                <p className="text-muted-foreground mt-1">
                  This inward order has been Revised. No further GRNs can be
                  created under this Order.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="p-6 border-b">
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <h1
                className={`text-lg font-semibold text-gray-900 ${
                  isRevised ? "border-l-4 border-l-primary pl-2" : ""
                }`}
              >
                {inward_order_id}
              </h1>

              {inwardOrder.type && (
                <span className="px-2.5 py-0.5 rounded-full border text-xs font-semibold text-gray-700 bg-gray-50">
                  {inwardOrder.type}
                </span>
              )}

              {inwardOrder.status && (
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${getInwardStatusClass(
                    inwardOrder.status,
                  )}`}
                >
                  {inwardOrder.status}
                </span>
              )}

              {isRevised && (
                <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors status-revised">
                  <Icon path={mdiHistory} size={0.6} className="mr-1" />
                  Revised
                </span>
              )}

              {inwardOrder.past_disputes &&
                inwardOrder.past_disputes.some((d) => d.status === "open") && (
                  <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors status-disputed">
                    <Icon path={mdiAlertOutline} size={0.6} className="mr-1" />
                    Disputed
                  </span>
                )}

              {type === "ST" &&
                inwardOrder.st_subtype === StockTransferSubtype.REPLACEMENT && (
                  <span className="px-2 py-0.5 rounded-full border border-yellow-400 bg-yellow-100 text-yellow-700 text-xs font-semibold">
                    Replacement ST
                  </span>
                )}

              {type === "ST" &&
                inwardOrder.st_subtype === StockTransferSubtype.RETURNABLE && (
                  <span className="px-2 py-0.5 rounded-full border border-yellow-400 bg-yellow-100 text-yellow-700 text-xs font-semibold">
                    Returnable ST
                  </span>
                )}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mt-6">
            <div>
              <p className="text-gray-500 flex gap-1 items-center">
                <Icon path={mdiWarehouse} className="h-4 w-4 text-blue-600" />
                From
              </p>
              <p className="font-semibold">{inwardOrder.from?.name}</p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1 items-center">
                <Icon path={mdiWarehouse} className="h-4 w-4 text-green-600" />
                To Warehouse
              </p>
              <p className="font-semibold">{inwardOrder.toWarehouse?.name}</p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1 items-center">
                <Icon
                  path={mdiCalendarBlankOutline}
                  className="h-4 w-4 text-purple-600"
                />
                Expected Date
              </p>
              <p className="font-semibold">
                {formatDateYYYYMMDD(inwardOrder.expectedDate)}
              </p>
            </div>

            <div>
              <p className="text-gray-500 flex gap-1 items-center">
                <Icon
                  path={mdiPackageVariantClosed}
                  className="h-4 w-4 text-orange-600"
                />
                Total Value
              </p>
              <p className="font-semibold">
                ₹ {formatAmountWithCommas(inwardOrder.totalPrice)}
                <CurrencyBadge
                  currency={inwardOrder?.currency}
                  rate={inwardOrder?.vendor_currency_to_inr}
                  inrAmount={inwardOrder?.totalPrice}
                />
              </p>
              {computeInwardTaxTotals(inwardOrder?.items).gst > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Incl. GST: ₹{" "}
                  {formatAmountWithCommas(
                    computeInwardTaxTotals(inwardOrder?.items).total,
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full gap-2 bg-blue-100 p-2 rounded-md mt-2">
          {[
            {
              key: "items",
              label: `Items (${inwardItems?.length})`,
            },
            {
              key: "grn-history",
              label: `GRN History (${linkedPoInwards ? linkedPoInwards.length : 0})`,
            },
          ].map((t) => (
            <button
              key={t.key}
              id={`warehouse-inward-infra-receive-tab-${t.key}`}
              onClick={() => setActiveTab(t.key as MainTabType)}
              className={`px-4 w-1/2 py-2 rounded-md text-sm font-bold ${
                activeTab === t.key ? "bg-white shadow-sm" : "text-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 flex flex-col gap-4">
          {activeTab === "items" && (
            <React.Fragment>
              <PastDisputes disputes={inwardOrder.past_disputes || []} />

              <div className="space-y-4">
                <div className="flex flex-row justify-between ">
                  <h3 className="font-bold flex gap-1 items-center">
                    <Icon
                      path={mdiPackageVariantClosed}
                      className="h-5 w-5 text-orange-600"
                    />
                    Order Items
                  </h3>
                </div>
                {/* Header Row */}
                <div className="hidden md:grid grid-cols-12 gap-4 items-center bg-gray-100 border rounded-xl px-4 py-3 text-sm font-semibold text-gray-600">
                  <div className="col-span-1 ">
                    <input
                      id="warehouse-inward-infra-receive-select-all-desktop"
                      type="checkbox"
                      checked={
                        selectedItems.size > 0 &&
                        selectedItems.size ===
                          inwardItems.filter(
                            (item, index) =>
                              getPendingQty(
                                item,
                                getInwardItemRowKey(item, index),
                              ) > 0,
                          ).length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allRowKeys: string[] = [];
                          const updatedPhysical: any = {};
                          const updatedInvoice: any = {};
                          const updatedSelection: any = {};

                          inwardItems.forEach((item, index) => {
                            const rowKey = getInwardItemRowKey(item, index);
                            const pending = getPendingQty(item, rowKey);

                            if (pending === 0) return;

                            allRowKeys.push(rowKey);
                            updatedPhysical[rowKey] = pending;
                            if (item.subCategory !== "Finished Goods") {
                              updatedInvoice[rowKey] = pending;
                            }

                            updatedSelection[rowKey] = {
                              matched: [],
                              unmatched: [],
                              quantity: pending,
                            };
                          });

                          setSelectedItems(new Set(allRowKeys));
                          setPhysicalQtys(updatedPhysical);
                          setInvoiceQtys(updatedInvoice);
                          setDeviceSelections(updatedSelection);
                        } else {
                          // ✅ Clear everything
                          setSelectedItems(new Set());
                          setPhysicalQtys({});
                          setInvoiceQtys({});
                          setDeviceSelections({});
                        }
                      }}
                    />
                  </div>

                  <div className="col-span-2">Item</div>

                  <div className="col-span-1 text-center">PO Qty</div>

                  <div className="col-span-1 text-center">Prev. Recd</div>

                  <div className="col-span-1 text-center">Remaining</div>

                  <div className="col-span-1 text-center">Invoice Qty</div>

                  <div className="col-span-1 text-center">Rate</div>

                  <div className="col-span-1 text-center">Physical Qty</div>

                  <div className="col-span-2 text-center">Value</div>

                  <div className="col-span-1 text-center">Status</div>
                </div>

                {/* Mobile select-all row */}
                <div className="md:hidden flex items-center gap-2 px-1 py-2 text-sm font-semibold text-gray-600">
                  <input
                    id="warehouse-inward-infra-receive-select-all-mobile"
                    type="checkbox"
                    className="w-4 h-4"
                    checked={
                      selectedItems.size > 0 &&
                      selectedItems.size ===
                        inwardItems.filter(
                          (item, index) =>
                            getPendingQty(
                              item,
                              getInwardItemRowKey(item, index),
                            ) > 0,
                        ).length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allRowKeys: string[] = [];
                        inwardItems.forEach((item, index) => {
                          const rowKey = getInwardItemRowKey(item, index);
                          if (getPendingQty(item, rowKey) > 0) {
                            allRowKeys.push(rowKey);
                          }
                        });
                        setSelectedItems(new Set(allRowKeys));
                      } else {
                        setSelectedItems(new Set());
                        setPhysicalQtys({});
                        setInvoiceQtys({});
                        setDeviceSelections({});
                      }
                    }}
                  />
                  <span>Select All Items</span>
                </div>

                {selectedItems.size > 0 && (
                  <div className="flex items-center justify-between bg-blue-50 border rounded-lg px-4 py-2">
                    <p className="text-sm font-medium">
                      {selectedItems.size} item(s) selected
                    </p>

                    <div className="flex gap-2">
                      {/* Accept Selected */}
                      <button
                        id="warehouse-inward-infra-receive-accept-selected-btn"
                        onClick={() => {
                          const updatedPhysical = { ...physicalQtys };
                          const updatedSelection = { ...deviceSelections };

                          selectedItems.forEach((rowKey) => {
                            const itemIndex = inwardItems.findIndex(
                              (i, index) =>
                                getInwardItemRowKey(i, index) === rowKey,
                            );
                            const item =
                              itemIndex >= 0 ? inwardItems[itemIndex] : null;

                            if (!item) return;

                            const pending = getPendingQty(item, rowKey);

                            updatedPhysical[rowKey] = pending;

                            updatedSelection[rowKey] = {
                              ...(updatedSelection[rowKey] || {
                                matched: [],
                                unmatched: [],
                              }),
                              quantity: pending,
                            };
                          });

                          setPhysicalQtys(updatedPhysical);
                          setDeviceSelections(updatedSelection);
                        }}
                        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Accept Selected
                      </button>

                      {/* Clear */}
                      <button
                        id="warehouse-inward-infra-receive-clear-selected-btn"
                        onClick={() => {
                          const updatedPhysical = { ...physicalQtys };
                          const updatedSelection = { ...deviceSelections };
                          const updatedInvoice = { ...invoiceQtys };

                          selectedItems.forEach((rowKey) => {
                            delete updatedPhysical[rowKey];
                            delete updatedSelection[rowKey];
                            delete updatedInvoice[rowKey];
                          });

                          setPhysicalQtys(updatedPhysical);
                          setDeviceSelections(updatedSelection);
                          setInvoiceQtys(updatedInvoice);
                          setSelectedItems(new Set());
                        }}
                        className="bg-orange-600 text-black px-3 py-1 rounded-md text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
                {inwardItems?.map((item: ItemWarehouseModel, index) => {
                  const rowKey = getInwardItemRowKey(item, index);
                  const effectiveReceivedQty = receivedQtyByRowKey[rowKey] ?? 0;
                  const effectiveItem = {
                    ...item,
                    receivedQty: effectiveReceivedQty,
                  };
                  const pending = getPendingQty(item, rowKey);

                  return (
                    <InfraInwardReceiveItemCard
                      key={rowKey}
                      item={effectiveItem}
                      currency={inwardOrder?.currency}
                      vendor_currency_to_inr={
                        inwardOrder?.vendor_currency_to_inr
                      }
                      isSelected={selectedItems.has(rowKey)}
                      onSelect={(checked) => {
                        setSelectedItems((prev) => {
                          const updated = new Set(prev);
                          checked
                            ? updated.add(rowKey)
                            : updated.delete(rowKey);
                          return updated;
                        });

                        if (checked) {
                          setPhysicalQtys((prev) => ({
                            ...prev,
                            [rowKey]: pending,
                          }));
                          if (item.subCategory !== "Finished Goods") {
                            setInvoiceQtys((prev) => ({
                              ...prev,
                              [rowKey]: pending,
                            }));
                          }
                          setDeviceSelections((prev) => ({
                            ...prev,
                            [rowKey]: {
                              ...(prev[rowKey] || {
                                matched: [],
                                unmatched: [],
                              }),
                              quantity: pending,
                            },
                          }));
                        } else {
                          setPhysicalQtys((prev) => {
                            const copy = { ...prev };
                            delete copy[rowKey];
                            return copy;
                          });

                          setInvoiceQtys((prev) => {
                            const copy = { ...prev };
                            delete copy[rowKey];
                            return copy;
                          });

                          setDeviceSelections((prev) => {
                            const copy = { ...prev };
                            delete copy[rowKey];
                            return copy;
                          });
                        }
                      }}
                      allocatedDevices={item.deviceIds ?? []}
                      inwardedDevices={getInwardedDevicesForItem(item)}
                      error={qtyErrors[rowKey]}
                      onDeviceSelection={(selection) =>
                        handleDeviceSelection(rowKey, item, selection)
                      }
                      invoiceQty={invoiceQtys[rowKey]}
                      invoiceRate={invoiceRates[rowKey]}
                      physicalQty={physicalQtys[rowKey]}
                      onInvoiceQtyChange={(qty) =>
                        handleInvoiceQtyChange(rowKey, qty)
                      }
                      onRateChange={(rate) => handleRateChange(rowKey, rate)}
                      onPhysicalQtyChange={(qty) =>
                        handlePhysicalQtyChange(rowKey, item, qty)
                      }
                      invoiceTotalAmount={invoiceTotalAmounts[rowKey]}
                      onTotalAmountChange={(val) =>
                        handleTotalAmountChange(rowKey, val)
                      }
                    />
                  );
                })}
              </div>

              {/* Order Level Tax Breakdown Summary only for PO */}
              {type === "PO" &&
                (orderTotals.total > 0 || orderTotals.subtotal > 0) && (
                  <div className="bg-white/50 p-3 rounded-md border border-dashed border-gray-300 space-y-2">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Subtotal</span>
                      <span>
                        ₹ {formatAmountWithCommas(orderTotals.subtotal)}
                      </span>
                    </div>
                    {orderTotals.cgst > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>CGST</span>
                        <span>
                          + ₹{formatAmountWithCommas(orderTotals.cgst)}
                        </span>
                      </div>
                    )}
                    {orderTotals.sgst > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>SGST</span>
                        <span>
                          + ₹{formatAmountWithCommas(orderTotals.sgst)}
                        </span>
                      </div>
                    )}
                    {orderTotals.igst > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>IGST</span>
                        <span>
                          + ₹{formatAmountWithCommas(orderTotals.igst)}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-1 flex justify-between text-sm font-semibold text-gray-800">
                      <span>Expected Calculated Total</span>
                      <span>₹ {formatAmountWithCommas(orderTotals.total)}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 italic text-right mt-1">
                      * Values calculated based on provided quantities and rates
                    </p>
                  </div>
                )}

              {/* Invoice Number  only mandatory for PO */}
              <div className="space-y-4">
                {type === "PO" && (
                  <div>
                    <label className="text-sm font-semibold">
                      Invoice Number <RequiredMark />
                    </label>
                    <input
                      id="warehouse-inward-infra-receive-invoice-number"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      disabled={isSubmitting}
                      className="mt-1 w-full border rounded px-3 py-2 disabled:bg-gray-100"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold">
                    {type === "PO" ? (
                      <>
                        Attach Invoice <RequiredMark />
                      </>
                    ) : (
                      <>
                        Attach Documents <RequiredMark />
                      </>
                    )}
                  </label>
                  <InwardAttachmentUpload
                    accept="application/pdf,image/jpeg,image/png"
                    maxSizeMB={10}
                    files={attachments}
                    multiple
                    onChange={setAttachments}
                  />
                </div>
              </div>
            </React.Fragment>
          )}

          {/* Linked Inwards (PO only) */}
          {activeTab === "grn-history" && (
            <div>
              {linkedPoInwards && linkedPoInwards.length > 0 && (
                <LinkedInwardsTable data={linkedPoInwards} />
              )}
              {linkedPoInwards && linkedPoInwards.length === 0 && (
                <div className="flex flex-col text-gray-700 gap-1 align-middle justify-center items-center py-10">
                  <p>No GRNs recorded yet</p>
                  <p>Complete a GRN to see the history here</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer actions */}
      <div className="sticky bottom-0 bg-white border-t shadow px-4 py-4">
        <div className="max-w-5xl mx-auto flex justify-end gap-3">
          <button
            id="warehouse-inward-infra-receive-cancel-btn"
            onClick={() => history.push(`${urls.warehouse_inward}`)}
            disabled={isSubmitting}
            className={`border px-4 py-2 rounded transition-all ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>

          {type === "PO" && (
            <button
              id="warehouse-inward-infra-receive-raise-dispute-btn"
              onClick={() => setIsDisputeOpen(true)}
              disabled={isSubmitting || loading || isBlockedByDispute}
              className={`flex items-center gap-2 border border-yellow-300 text-yellow-500 bg-yellow-50 hover:bg-yellow-100 px-4 py-2 rounded-lg font-semibold transition-all
              ${isSubmitting || loading || isBlockedByDispute ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Icon path={mdiAlertOutline} className="h-4 w-4" />
              Raise Dispute
            </button>
          )}

          <button
            id="warehouse-inward-infra-receive-submit-btn"
            onClick={handleOpenConfirmation}
            disabled={isSubmitting || loading || isBlockedByDispute}
            className={`flex items-center gap-2 font-semibold px-5 py-2 rounded-lg transition-all ${
              isSubmitting || loading || isBlockedByDispute
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95 cursor-pointer"
            }`}
          >
            <Icon path={mdiCheckCircleOutline} className="h-4 w-4" />
            {isSubmitting ? "Submitting..." : "Record Inward"}
          </button>
        </div>

        {isBlockedByDispute && (
          <div className="flex items-center justify-end gap-1.5 px-1 py-0.5 ">
            <Icon path={mdiAlertCircle} size={0.5} color={"red"} />
            <span className="text-xs text-color-destructive">
              Recording blocked due to unresolved dispute
            </span>
          </div>
        )}
      </div>

      {type === "PO" && (
        <RaiseDisputeModal
          isOpen={isDisputeOpen}
          onClose={() => setIsDisputeOpen(false)}
          onSubmit={handleDisputeSubmit}
          inwardOrder={inwardOrder}
          invoiceQtys={invoiceQtys}
          invoiceRates={invoiceRates}
          physicalQtys={physicalQtys}
          deviceSelections={deviceSelections}
          isSubmitting={isDisputeSubmitting}
        />
      )}

      {showConfirmModal && (
        <ConfirmationModal
          show={showConfirmModal}
          title="Confirm Record Inward"
          message="Are you sure you want to record this inward? This will generate a new GRN."
          data={[
            { label: "Order ID", value: inward_order_id },
            { label: "Invoice Number", value: invoiceNumber || "N/A" },
            {
              label: "Items Count",
              value: `${Object.values(deviceSelections).reduce(
                (acc, s) => acc + s.quantity,
                0,
              )} units`,
            },
            { label: "Attachments", value: `${attachments.length} files` },
          ]}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmModal(false)}
          isSubmitting={isSubmitting}
          confirmText="Confirm & Record Inward"
        />
      )}

      {loading && <LoadingWidget />}
    </div>
  );
};

export default InfraPOInwardReceivePage;
